'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Star, Quote } from 'lucide-react';

const testimonial = {
  quote: "MANGQASHA IT SOLUTIONS helped me launch my online store in a weekend. The AI tools wrote all my product descriptions and my sales went up 60% in the first month.",
  name: "Priya Nair",
  business: "Priya's Leather Goods, Austin TX",
  rating: 5,
};

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-5/12 gradient-primary flex-col justify-between p-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute bottom-32 right-10 w-40 h-40 rounded-full border border-white" />
          <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-white/20" />
        </div>

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <span className="text-2xl font-800 text-white tracking-tight">MANGQASHA IT SOLUTIONS</span>
        </div>

        {/* Hero text */}
        <div className="relative z-10">
          <h1 className="text-hero-xl text-white mb-4">
            Your business,<br />online in minutes.
          </h1>
          <p className="text-white/75 text-lg leading-relaxed max-w-sm">
            Build a beautiful website, manage your store, track competitors, and accept bookings — all powered by AI.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {['AI Website Builder', 'E-commerce Store', 'Competitor Radar', 'Online Bookings', 'Social Selling']?.map((f) => (
              <span key={`feat-pill-${f}`} className="px-3 py-1.5 bg-white/15 text-white text-xs font-600 rounded-full backdrop-blur-sm">
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
          <Quote size={20} className="text-white/50 mb-2" />
          <p className="text-white/90 text-sm leading-relaxed mb-3">"{testimonial?.quote}"</p>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-700 text-sm">
              PN
            </div>
            <div>
              <p className="text-white text-sm font-700">{testimonial?.name}</p>
              <p className="text-white/60 text-xs">{testimonial?.business}</p>
            </div>
            <div className="ml-auto flex gap-0.5">
              {Array.from({ length: testimonial?.rating })?.map((_, i) => (
                <Star key={`star-${i + 1}`} size={12} className="text-accent fill-accent" />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 bg-background">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="text-xl font-800 text-foreground">MANGQASHA IT SOLUTIONS</span>
        </div>

        <div className="w-full max-w-sm">
          {/* Mode toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-150 ${
                mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-150 ${
                mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignup={() => setMode('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}